<script lang="ts">
import { computed, defineComponent, inject, ref } from "vue";
import Ueberschrift from "@/components/grid/Ueberschrift.vue";
import Zeile from "@/components/grid/Zeile.vue";
import TitelZelle from "@/components/grid/TitelZelle.vue";
import Zelle from "@/components/grid/Zelle.vue";
import ValidierungsFehler from "@/components/ValidierungsFehler.vue";
import InfoBox from "@/components/grid/InfoBox.vue";
import HugOrtsvereine from "@/components/hug/HugOrtsvereine";
import Dropdown from "@/components/Vorschlagsliste/Dropdown.vue";
import { Dropdowndaten } from "@/model/Dropdowndaten";
import { useFormStore } from "@/businesslogic/stores/formStore";

export default defineComponent({
  name: "HugMitgliedschaft",
  computed: {
    Dropdowndaten() {
      return Dropdowndaten;
    },
  },
  components: {
    InfoBox,
    ValidierungsFehler,
    Zelle,
    TitelZelle,
    Zeile,
    Ueberschrift,
    Dropdown,
  },
  props: {
    fehler: Object,
  },
  setup(props) {
    const piniaStore = useFormStore();
    const wechsleSichtbarkeit: any = inject("wechsleSichtbarkeit");
    const alleOrtsvereine = ref(HugOrtsvereine);

    const ueberschriftText = computed(() => {
      return piniaStore.state.isMakler ? "Haus & Grund-Mitgliedschaft" : "Ihre Haus & Grund-Mitgliedschaft";
    });

    const titel = computed(() => {
      return piniaStore.state.isMakler
        ? "Der Antragsteller ist Mitglied in einem Haus & Grund-Verein"
        : "Sie sind Mitglied in einem Haus & Grund-Verein";
    });

    const updateHugMitgliedOrt = (value: any) => {
      piniaStore.state.personendaten.versicherungsnehmer.hugMitgliedOrt = value;
    };

    const dropdowndatenHuGOrt = new Dropdowndaten(
      alleOrtsvereine.value,
      2,
      100,
      props.fehler,
      piniaStore.state.tarifinformationen.hugMitglied,
      "Ort des Vereins",
      "Bitte geben Sie den Namen des Vereins ein (max. 100 Zeichen).",
    );

    return {
      piniaStore,
      wechsleSichtbarkeit,
      titel,
      ueberschriftText,
      alleOrtsvereine,
      updateHugMitgliedOrt,
      dropdowndatenHuGOrt,
    };
  },
});
</script>

<template>
  <div>
    <ueberschrift :titel="ueberschriftText" />

    <zeile :spacing-y="false">
      <titel-zelle :zeige-info="true" info-box-id="tariffDiscountHuGInfo">{{ titel }}</titel-zelle>
      <zelle class="mkRadio">
        <input
          v-model="piniaStore.state.tarifinformationen.hugMitglied"
          type="radio"
          id="tariffDiscountHuGYes"
          name="tariffDiscountHuG"
          v-bind:value="true"
          data-cy="tariffDiscountHuGYes"
          aria-label="Ja, ich bin Haus und Grund Mitglied"
        />
        <label for="tariffDiscountHuGYes" class="radiolabel">Ja</label>
        <input
          v-model="piniaStore.state.tarifinformationen.hugMitglied"
          type="radio"
          id="tariffDiscountHuGNo"
          name="tariffDiscountHuG"
          v-bind:value="false"
          data-cy="tariffDiscountHuGNo"
          aria-label="Nein, ich bin kein Haus und Grund Mitglied"
          aria-describedby="tariffDiscountHuGInfo"
        />
        <label for="tariffDiscountHuGNo" class="radiolabel">Nein</label>
        <validierungs-fehler :fehler-text="fehler.tariffDiscountHuG" :reduzierter-abstand="true" />
        <info-box role="tooltip" id="tariffDiscountHuGInfo">
          Der Eigentümerverband Haus &amp; Grund vertritt die Interessen privater Haus-, Wohnungs- und Grundeigentümer.
          Dem Zentralverband Haus &amp; Grund Deutschland mit Sitz in Berlin sind die über 20 Landesverbände und ca. 900
          Ortsvereine mit rund 900.000 Mitgliedern angeschlossen. Als Haus &amp; Grund-Mitglied erhalten Sie in allen
          Sparten 10% Rabatt."
        </info-box>
      </zelle>
    </zeile>

    <div v-if="piniaStore.state.tarifinformationen.hugMitglied">
      <zeile>
        <titel-zelle>Ort des Vereins</titel-zelle>
        <zelle>
          <dropdown
            name="huGOrt"
            :wert-aktuell="piniaStore.state.personendaten.versicherungsnehmer.hugMitgliedOrt"
            :dropdowndaten="dropdowndatenHuGOrt"
            @updateVorschlag="updateHugMitgliedOrt"
          />
          <validierungs-fehler :fehler-text="fehler?.huGOrt" />
        </zelle>
      </zeile>

      <zeile>
        <titel-zelle>Mitgliedsnummer</titel-zelle>
        <zelle>
          <input
            type="text"
            id="hugMembershipNumber"
            name="hugMembershipNumber"
            v-model.lazy.trim="piniaStore.state.personendaten.versicherungsnehmer.hugMitgliedNummer"
            class="c-form-control__input"
            placeholder="Mitgliedsnummer"
            minlength="2"
            maxlength="10"
            :required="piniaStore.state.tarifinformationen.hugMitglied"
            :aria-required="piniaStore.state.tarifinformationen.hugMitglied"
            data-fehlermeldung="Bitte geben Sie Ihre Mitgliedsnummer ein (max. 10 Zeichen)."
            data-cy="hugMitgliedNummer"
          />
          <validierungs-fehler :fehler-text="fehler?.hugMembershipNumber" />
        </zelle>
      </zeile>
    </div>
  </div>
</template>

<style scoped></style>
